import { Box, CircularProgress } from '@mui/material';
import { config } from 'appConfig';
import { userThemeVar } from 'client/cache';
import { HomeLayout } from 'components/Layouts/HomeLayout';
import { ProtectedLayout } from 'components/Layouts/ProtectedLayout';
import { NotFound } from 'components/NotFound';
import { ProtectedRoutes } from 'components/routing/ProtectedRoutes';
import { usePostHog } from 'posthog-js/react';
import { FunctionComponent, lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

const LazyAuthPage = lazy(() => import('pages/Auth/AuthPage').then(({ AuthPage }) => ({ default: AuthPage })));

const LazyLogoutPage = lazy(() =>
  import('components/pages/Auth/LogoutPage').then(({ LogoutPage }) => ({ default: LogoutPage })),
);

const LazyDdtDashboard = lazy(() => import('ddt-dashboard/default').then(({ Index }) => ({ default: Index })));

const LazyThryvDdtDashboard = lazy(() => import('ddt-dashboard/thryv').then(({ Index }) => ({ default: Index })));

// const LazyManageFBPages = lazy(() =>
//   import('pages/ManageFBPages/ManageFBPages').then(({ ManageFBPages }) => ({ default: ManageFBPages })),
// );

const LazyManageFBPagesV2 = lazy(() =>
  import('pages/ManageFBPagesV2/ManageFBPagesV2').then(({ ManageFBPagesV2 }) => ({ default: ManageFBPagesV2 })),
);

export const MANAGE_FB_PAGE_PERMISSIONS_ROUTE_URL = 'manage-facebook-page-permissions';
export const MANAGE_FB_PAGE_PERMISSIONS_ROUTE_URL_V2 = 'manage-facebook-page-permissions/v2';

// For when external login pages redirect to /login_success
const SocialLoginSuccess: FunctionComponent = () => {
  useEffect(() => {
    window.localStorage.setItem('loginSuccessFlag', 'true');

    window.close();
  }, []);

  return null;
};

const Loading: FunctionComponent = () => (
  <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh">
    <CircularProgress size="5rem" thickness={2.5} />
  </Box>
);

export const AppRouting: FunctionComponent = () => {
  const appTheme = userThemeVar();
  const { pathname } = useLocation();
  const posthogClient = usePostHog();

  /**
   * This is a posthog event.
   *
   * @see {@link https://posthog.com/docs/integrate/client/js#one-page-apps-and-page-views}
   */
  useEffect(() => {
    if (config.posthogShouldTrack && posthogClient) {
      posthogClient.capture('$pageview', { url: pathname });
    }
  }, [pathname, posthogClient]);

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Non-authenticated user routes */}
        <Route element={<HomeLayout />}>
          <Route path="/" element={<Navigate to="/auth/login" />} />
          <Route path="/login_success" element={<SocialLoginSuccess />} />
          <Route path="/auth/*" element={<LazyAuthPage />} />
        </Route>
        {/* Authenticated-users only */}
        <Route element={<ProtectedLayout />}>
          <Route path="*" element={<ProtectedRoutes />} />
        </Route>
        <Route path="/direct/*" element={appTheme === 'thryv' ? <LazyThryvDdtDashboard /> : <LazyDdtDashboard />} />
        <Route path="/auth/logout" element={<LazyLogoutPage />} />
        <Route path="/manage-facebook-page-permissions/*" element={<LazyManageFBPagesV2 />} />
        <Route path="/manage-facebook-page-permissions/v2/*" element={<LazyManageFBPagesV2 />} />
        {/* Fallback: not found page */}
        <Route path="/not-found" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/not-found" replace />} />
      </Routes>
    </Suspense>
  );
};
